import { useMemo, useRef } from "react"
import { useFrame } from "@react-three/fiber"
import * as THREE from "three"

// 2026-05-06 v3 — Reescrito desde cero como CILINDRO SIMPLE.
//
// Versiones anteriores (v1 línea fina cyan, v2 tubo Bezier de 3 capas
// con shaders custom + AdditiveBlending + depthTest:false) provocaban
// pantalla negra cuadrada cuando aparecía el haz al solicitar enlace.
// Ya intentamos guardas defensivas (skip por distancia degenerada,
// clamp de cámara, fallback de Domo, etc.) sin resolver el bug — el
// patrón apuntaba a corrupción del pipeline cuando montaban shaders
// custom + materiales transparentes con depthTest:false dentro del
// EffectComposer.
//
// Esta v3 usa la combinación más robusta y probada de Three.js:
//   · Geometría: una sola CylinderGeometry orientada con quaternion
//     (sin Bezier, sin Frenet frames).
//   · Material: MeshBasicMaterial (sin lighting, sin shaders custom).
//   · Blending normal con depthWrite:false (no ocluye, pero respeta
//     z-buffer en el render del scene).
//   · Sin AdditiveBlending (que sumado a depthTest:false en algunas
//     GPUs / postprocessing combos rompe el frame).
// Si en el futuro queremos volver a un haz pulsante con núcleo blanco,
// se puede sumar un segundo cilindro más fino con color #fff y un
// useFrame que mueva su escala — pero como capa cosmética sobre esta
// base estable, no como sustituto.
//
// El haz crece desde `from` hacia `to` según `completion` (0..1):
// 0.6 mientras es solicitud unidireccional, 1 cuando se reciproca.

interface LinkBeamProps {
    from: [number, number, number]
    to: [number, number, number]
    color?: string
    /**
     * 0 = recién iniciado (haz a medias).
     * 1 = recíproco (alcanza al otro lado).
     */
    completion?: number
    /** No usado por v3 — se conserva la prop para no romper callers. */
    height?: number
}

const RADIUS = 0.07
const SEGMENTS = 12
const MIN_BEAM_DISTANCE = 0.2

const TMP_FROM = new THREE.Vector3()
const TMP_TO = new THREE.Vector3()
const TMP_DIR = new THREE.Vector3()
const TMP_QUAT = new THREE.Quaternion()
const TMP_UP = new THREE.Vector3(0, 1, 0)

export function LinkBeam({
    from,
    to,
    color = "#00E5FF",
    completion = 1,
}: LinkBeamProps) {
    const meshRef = useRef<THREE.Mesh>(null)

    // Calculamos posición/orientación/longitud por frame leyendo refs
    // de las props — evitamos useMemo+nuevo Vector3 en cada render
    // (los avatares se mueven y el haz tiene que seguirlos suavemente).
    const c = Math.max(0.1, Math.min(1, completion))

    useFrame(() => {
        const mesh = meshRef.current
        if (!mesh) return
        TMP_FROM.set(from[0], 1, from[2])
        TMP_TO.set(to[0], 1, to[2])
        const fullDist = TMP_FROM.distanceTo(TMP_TO)
        if (fullDist < MIN_BEAM_DISTANCE) {
            mesh.visible = false
            return
        }
        // Punto de la punta del haz. Si completion < 1, el haz no
        // llega al otro avatar todavía.
        const segLen = fullDist * c
        if (segLen < MIN_BEAM_DISTANCE) {
            mesh.visible = false
            return
        }
        mesh.visible = true
        TMP_DIR.copy(TMP_TO).sub(TMP_FROM).normalize()
        // Punto medio del segmento desde TMP_FROM extendido `segLen`.
        // Posición de la cilindro = midpoint del segmento que se pinta.
        const midX = TMP_FROM.x + TMP_DIR.x * segLen * 0.5
        const midY = TMP_FROM.y + TMP_DIR.y * segLen * 0.5
        const midZ = TMP_FROM.z + TMP_DIR.z * segLen * 0.5
        mesh.position.set(midX, midY, midZ)
        // CylinderGeometry está orientado con su eje vertical (Y).
        // Rotamos al `up` del cilindro hacia la dirección del haz.
        TMP_QUAT.setFromUnitVectors(TMP_UP, TMP_DIR)
        mesh.quaternion.copy(TMP_QUAT)
        // La altura del cilindro es 1 por defecto; escalamos en Y a
        // segLen y dejamos X/Z en 1 (el RADIUS ya está hardcoded en
        // la geometría).
        mesh.scale.set(1, segLen, 1)
    })

    // 2026-05-06 — useMemo solo para evitar recrear el material cada
    // render. La geometría también se memoriza con altura 1 y la
    // escalamos por frame.
    const material = useMemo(
        () =>
            new THREE.MeshBasicMaterial({
                color,
                transparent: true,
                opacity: 0.92,
                depthWrite: false,
                toneMapped: false,
            }),
        [color]
    )
    const geometry = useMemo(
        () => new THREE.CylinderGeometry(RADIUS, RADIUS, 1, SEGMENTS, 1, true),
        []
    )

    return (
        <mesh ref={meshRef} geometry={geometry} material={material} />
    )
}
